import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { extractErrorMessage } from '../utils/errorUtils';

// Configuration de l'URL de base de l'API
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
console.log('🌐 API_BASE_URL:', API_BASE_URL);

// Créer une instance axios avec la configuration de base
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 secondes
});

// Intercepteur pour ajouter le token JWT à chaque requête
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Récupérer le token depuis le localStorage
    const authData = localStorage.getItem('momo_auth');
    if (authData) {
      try {
        const { token } = JSON.parse(authData);
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Erreur lors de la récupération du token:', error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Variable pour éviter les redirections multiples
let isRedirecting = false;

// Intercepteur pour gérer les erreurs de réponse
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      // Le serveur a répondu avec un code d'erreur
      switch (error.response.status) {
        case 401:
        case 403:
          // Non autorisé - Token expiré ou invalide (401) ou Accès refusé/Session expirée (403)
          console.warn('🔒 Session expirée ou accès refusé');

          // Éviter les déconnexions multiples
          if (!isRedirecting) {
            isRedirecting = true;

            // Sauvegarder le chemin actuel pour y revenir après la reconnexion
            const currentPath = window.location.pathname + window.location.search;
            // Ne sauvegarder que si ce n'est pas déjà la page de login
            if (!currentPath.startsWith('/login')) {
              sessionStorage.setItem('redirectAfterLogin', currentPath);
              console.log('🔒 Chemin sauvegardé pour redirection:', currentPath);
            }

            // Dispatcher un événement personnalisé pour notifier le store de se déconnecter
            console.log('🔒 Dispatch de l\'événement auth:expired');
            window.dispatchEvent(new CustomEvent('auth:expired'));

            // Réinitialiser le flag après un délai
            setTimeout(() => {
              isRedirecting = false;
            }, 1000);
          }

          // Rejeter l'erreur originale pour préserver les informations du backend
          return Promise.reject(error);

        case 402:
          // Payment Required - Abonnement nécessaire
          console.warn('💳 Abonnement requis pour cette action');
          // Afficher un message à l'utilisateur
          const message = extractErrorMessage(error, 'Abonnement requis pour accéder à cette fonctionnalité');
          // Stocker le message pour affichage
          sessionStorage.setItem('subscription_required_message', message);
          // Rediriger vers la page d'abonnement
          if (!window.location.pathname.startsWith('/subscription')) {
            window.location.href = '/subscription';
          }
          break;
        case 404:
          console.error('Ressource non trouvée');
          break;
        case 500:
          console.error('Erreur serveur');
          break;
        default:
          const errorMsg = extractErrorMessage(error, 'Une erreur est survenue');
          console.error('Erreur:', errorMsg);
      }
    } else if (error.request) {
      // La requête a été envoyée mais pas de réponse reçue
      console.error('Pas de réponse du serveur. Vérifiez votre connexion.');
    } else {
      // Erreur lors de la configuration de la requête
      console.error('Erreur:', error.message);
    }
    return Promise.reject(error);
  }
);

export default apiClient;
